import { useEffect, useState, useSyncExternalStore } from "react";

import { BREAKPOINTS } from "@/hooks/useBreakpoint";

/**
 * The CSS custom property the floating buttons (WhatsApp, voice) read to sit
 * above a bottom action bar. A bar publishes the distance from the viewport
 * bottom to its top edge here while it is in the buttons' corner; otherwise the
 * property is unset and the `0px` fallback applies.
 */
export const FLOATING_BUTTONS_LIFT_VAR = "--floating-buttons-lift";

/**
 * How far up from the viewport bottom the floating buttons' stack reaches, in
 * px, while only the WhatsApp and voice buttons are mounted. The single source
 * of the stack height: {@link floatingButtonsClearance} (the rem length shared
 * by the checkout column and the 404 page, which both scroll under the buttons)
 * is derived from it. Changing the FABs' offsets or size means changing this.
 */
export const FLOATING_BUTTONS_STACK_HEIGHT_PX = 120;

/**
 * What the order assistant button adds to the stack when it is mounted: its own
 * 44px plus a 12px gap (it sits at `bottom-[7.75rem]` = 124px). It is mounted
 * only for a signed-in salon with the service on, so the reserved height grows
 * by this only while {@link useAssistantFabMounted} says it is there.
 */
export const ASSISTANT_FAB_EXTRA_HEIGHT_PX = 56;

/**
 * The stack height with or without the order assistant button.
 */
export function floatingButtonsStackHeight(assistant: boolean): number {
  return (
    FLOATING_BUTTONS_STACK_HEIGHT_PX +
    (assistant ? ASSISTANT_FAB_EXTRA_HEIGHT_PX : 0)
  );
}

/**
 * The same height as a CSS length, for pages that pad or cap their own content
 * to stay clear of the buttons (checkout's summary column, the 404 page).
 */
export function floatingButtonsClearance(assistant: boolean): string {
  return `${floatingButtonsStackHeight(assistant) / 16}rem`;
}

/**
 * The corner the floating buttons occupy. These mirror class literals in
 * `WhatsAppSupportButton` / `VoiceOrderFab` / `OrderAssistantButton` that cannot
 * import them (`right-2` = 8px, `sm:right-6` = 24px, `w-11` = 44px): CHANGE THEM
 * TOGETHER with those classes, and with `FLOATING_BUTTONS_STACK_HEIGHT_PX` if a
 * FAB's size or `bottom` offset moves. The buttons' own `bottom` offset (0.75rem)
 * is the gap left between them and the bar's top edge, so every bar gets the
 * same gap.
 *
 * The stack, from the bottom: WhatsApp at 12px, voice at 68px and, when it is
 * mounted, the order assistant at 124px, each 44px tall with a 12px gap, plus an
 * 8px gap above the last. All three are the same 44px column: the assistant only
 * widens leftwards while hovered or focused, which is transient, so it is not
 * reserved for.
 */
const FAB_WIDTH_PX = 44;
const FAB_RIGHT_PX = 8;
const FAB_RIGHT_SM_PX = 24;

/**
 * The width of the strip along the viewport's right edge the buttons occupy
 * from `sm` up: their inset, their width and an 8px gap. A page whose content
 * must never sit under the buttons stops this far short of the right edge, so
 * the buttons sit in empty space and `floatingButtonsLift` reads 0 for it. Today
 * that is the cart page's content column (`CartPageColumn`), in every state from
 * `md` - the Table view most of all, where lifting the buttons would cover cells.
 * Derived from the corner constants above, so it cannot drift from them.
 */
export const FLOATING_BUTTONS_GUTTER_PX = FAB_RIGHT_SM_PX + FAB_WIDTH_PX + 8;

export interface LiftRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface LiftViewport {
  width: number;
  height: number;
}

export interface LiftOptions {
  /** The order assistant button is mounted, so the stack is one button taller
   *  ({@link ASSISTANT_FAB_EXTRA_HEIGHT_PX}). */
  assistant?: boolean;
}

/**
 * How far the floating buttons must rise to clear a bar: the distance from the
 * viewport bottom to the bar's top edge, but only when the bar actually sits in
 * the buttons' corner - horizontally under them and vertically within their
 * stack. A bar that stops short of the right edge (the cart's sticky checkout
 * bar on a wide screen), is scrolled out of view or sits above the stack leaves
 * them where they are. Pure: the hook measures, this decides.
 */
export function floatingButtonsLift(
  rect: LiftRect,
  viewport: LiftViewport,
  options: LiftOptions = {}
): number {
  const fabRight =
    viewport.width >= BREAKPOINTS.sm ? FAB_RIGHT_SM_PX : FAB_RIGHT_PX;
  const fabLeft = viewport.width - fabRight - FAB_WIDTH_PX;

  const overlapsCorner = rect.right > fabLeft && rect.left < viewport.width;
  const reachesStack =
    rect.top < viewport.height &&
    rect.bottom >
      viewport.height - floatingButtonsStackHeight(options.assistant ?? false);

  if (!overlapsCorner || !reachesStack) return 0;
  return Math.max(0, Math.round(viewport.height - rect.top));
}

// How many order assistant buttons are mounted (one in practice), and who to
// tell when that changes.
let assistantFabs = 0;
const assistantListeners = new Set<() => void>();

const hasAssistantFab = () => assistantFabs > 0;

function subscribeAssistantFab(listener: () => void) {
  assistantListeners.add(listener);
  return () => {
    assistantListeners.delete(listener);
  };
}

function setAssistantFabs(count: number) {
  const before = hasAssistantFab();
  assistantFabs = count;
  if (before !== hasAssistantFab()) {
    assistantListeners.forEach((listener) => listener());
  }
}

/**
 * Called by the order assistant button while it is mounted. The assistant is
 * absent for most sessions (service off, concierge operator, plugin missing), so
 * the stack height and the clearance pages keep are not allowed to assume it:
 * with it hidden they reserve exactly what they did before it existed.
 */
export function useAssistantFabMounted() {
  useEffect(() => {
    setAssistantFabs(assistantFabs + 1);
    return () => setAssistantFabs(assistantFabs - 1);
  }, []);
}

/**
 * The clearance a page must keep for the floating buttons right now, as a CSS
 * length: the stack with the order assistant button only while it is mounted.
 */
export function useFloatingButtonsClearance(): string {
  const assistant = useSyncExternalStore(
    subscribeAssistantFab,
    hasAssistantFab,
    () => false
  );
  return floatingButtonsClearance(assistant);
}

// Every mounted bar's current lift. The buttons rise by the largest, so two
// bars at once (a fixed bar over a page that also has a sticky one) never
// leave them under either.
const barLifts = new Map<symbol, number>();
let published = "";

function publish() {
  const lift = Math.max(0, ...barLifts.values());
  const next = lift > 0 ? `${lift}px` : "";
  if (next === published) return;
  published = next;
  const root = document.documentElement;
  if (next) {
    root.style.setProperty(FLOATING_BUTTONS_LIFT_VAR, next);
  } else {
    root.style.removeProperty(FLOATING_BUTTONS_LIFT_VAR);
  }
}

/**
 * Set on the document element while something modal is open over the page (the
 * cart's expanded summary sheet and its scrim, the order assistant, any
 * `DrawerPanel`). The floating buttons are `fixed` and `z-50`, so they would
 * otherwise stay undimmed above the scrim and cover the sheet's content; the
 * phone cart bar is not floating but would show in an inset drawer's margin.
 */
export const FLOATING_BUTTONS_HIDDEN_ATTR = "data-floating-buttons-hidden";

/**
 * The classes that make a floating button fade out while
 * {@link FLOATING_BUTTONS_HIDDEN_ATTR} is set: a literal here so Tailwind sees
 * it, shared by all the buttons (and the phone cart bar, which an inset drawer
 * would otherwise leave showing in its margin) so they cannot disagree. The
 * name stays "floating buttons" to match the attribute and
 * {@link useHideFloatingButtons}, which every caller already uses. `invisible`
 * takes them out of the tab order and the accessibility tree (opacity alone
 * leaves them focusable). The buttons transition `opacity` and `visibility`
 * ({@link FLOATING_BUTTONS_TRANSITION}, with `duration-300` on the element), so
 * the fade still plays before `visibility` flips.
 */
export const FLOATING_BUTTONS_HIDDEN_CLASS =
  "[html[data-floating-buttons-hidden]_&]:pointer-events-none [html[data-floating-buttons-hidden]_&]:invisible [html[data-floating-buttons-hidden]_&]:opacity-0";

/**
 * The transition the buttons share: their hover effects plus the fade that
 * {@link FLOATING_BUTTONS_HIDDEN_CLASS} drives, and `max-width` for the order
 * assistant's expansion on hover. A literal for Tailwind.
 */
export const FLOATING_BUTTONS_TRANSITION =
  "transition-[transform,background-color,box-shadow,opacity,visibility,max-width]";

// How many overlays currently want the buttons hidden, so one closing does not
// reveal them under another that is still open.
let hiders = 0;

/**
 * Hides the floating buttons while `hidden` is true. For a surface that dims
 * the page behind it, where buttons floating undimmed over the scrim would read
 * as part of the surface and cover its content. A bottom bar that sits in the
 * corner uses {@link useFloatingButtonsLift} so the buttons clear it; a bar that
 * an inset drawer would leave showing in its margin (the phone cart bar) also
 * hides on this attribute, so it uses both.
 */
export function useHideFloatingButtons(hidden: boolean) {
  useEffect(() => {
    if (!hidden) return;
    hiders += 1;
    document.documentElement.setAttribute(FLOATING_BUTTONS_HIDDEN_ATTR, "");
    return () => {
      hiders -= 1;
      if (hiders === 0) {
        document.documentElement.removeAttribute(FLOATING_BUTTONS_HIDDEN_ATTR);
      }
    };
  }, [hidden]);
}

/**
 * Lifts the floating buttons clear of a bottom action bar.
 *
 * The buttons are `fixed` in the bottom-right corner, the same corner a bottom
 * bar (the cart's Proceed to checkout, checkout's step action) puts its primary
 * action in. Reserving that corner with padding worked only while the action
 * was narrow enough to fit beside the buttons; a long total or a wider label
 * pushed it underneath them. Lifting the buttons above the bar removes the
 * shared corner altogether.
 *
 * Attach the returned ref to the bar's outermost element. The bar is measured
 * against the viewport (not assumed to be flush with its bottom), and
 * re-measured on resize, scroll and when the bar itself changes size, so a bar
 * that grows (the cart's expandable summary) or is `sticky` inside a padded
 * column is cleared by the same gap as a fixed one.
 */
export function useFloatingButtonsLift<T extends HTMLElement>() {
  const [bar, setBar] = useState<T | null>(null);

  useEffect(() => {
    if (!bar) return;

    const key = Symbol("floating-buttons-lift");
    const measure = () => {
      barLifts.set(
        key,
        floatingButtonsLift(
          bar.getBoundingClientRect(),
          { width: window.innerWidth, height: window.innerHeight },
          { assistant: hasAssistantFab() }
        )
      );
      publish();
    };
    measure();

    // Scroll fires many times per frame; measure at most once per frame so a
    // sticky bar does not rewrite the document-level property on every event.
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(schedule);
    observer?.observe(bar);
    // The assistant button mounts after its status request resolves, which
    // changes how tall the stack is under a bar that is already measured.
    const unsubscribeAssistant = subscribeAssistantFab(schedule);
    window.addEventListener("resize", schedule);
    // Capture, so a scrolling ancestor (not only the window) re-measures a
    // sticky bar as it sticks and unsticks.
    window.addEventListener("scroll", schedule, {
      capture: true,
      passive: true
    });

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      unsubscribeAssistant();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, { capture: true });
      barLifts.delete(key);
      publish();
    };
  }, [bar]);

  return setBar;
}
